import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FlaskConical } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useKiteStatus } from "@/components/Shell";
import RunReport from "@/pages/RunReport";

const iso = (d) => d.toISOString().slice(0, 10);

export default function Backtest() {
  const [kite] = useKiteStatus();
  const nav = useNavigate();
  const today = new Date();
  const [from, setFrom] = useState(iso(new Date(today.getTime() - 60 * 864e5)));
  const [to, setTo] = useState(iso(new Date(today.getTime() - 864e5)));
  const [source, setSource] = useState("sample");
  const [stopMode, setStopMode] = useState("");
  const [job, setJob] = useState(null);
  const [run, setRun] = useState(null);

  const touched = useRef(false);
  useEffect(() => { if (kite?.logged_in && !touched.current) setSource("kite"); }, [kite?.logged_in]);
  useEffect(() => {
    if (!job || job.status !== "running") return;
    const i = setInterval(async () => {
      const r = await api.get(`/backtest/jobs/${job.id}`);
      setJob(r.data);
      if (r.data.status === "done") setRun((await api.get(`/runs/${r.data.run_id}`)).data);
      if (r.data.status === "error") toast.error(r.data.error);
    }, 1000);
    return () => clearInterval(i);
  }, [job]);

  const start = async () => {
    setRun(null);
    try {
      const r = await api.post("/backtest", { date_from: from, date_to: to, source, overrides: stopMode ? { stop_mode: stopMode } : null });
      setJob(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inp = "mt-1 w-full h-10 rounded-md bg-[#0B111D] border border-[#1E293B] px-2 text-sm num text-slate-200";
  return (
    <div className="space-y-4">
      <div className="panel p-4" data-testid="backtest-config">
        <div className="flex items-center gap-2 mb-3"><FlaskConical size={16} className="text-cyan-300" /><h1 className="text-xl font-semibold text-slate-100">Backtest</h1>
          <span className="text-xs text-slate-500 ml-2">uses your Settings (sizing, limits, ticks)</span></div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <label className="text-[11px] text-slate-400">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inp} data-testid="bt-from-input" /></label>
          <label className="text-[11px] text-slate-400">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inp} data-testid="bt-to-input" /></label>
          <label className="text-[11px] text-slate-400">Data
            <select value={source} onChange={(e) => { touched.current = true; setSource(e.target.value); }} className={inp} data-testid="bt-source-select">
              <option value="kite">Kite 5-min history</option><option value="sample">SAMPLE (synthetic)</option>
            </select></label>
          <label className="text-[11px] text-slate-400">Stop mode
            <select value={stopMode} onChange={(e) => setStopMode(e.target.value)} className={inp} data-testid="bt-stopmode-select">
              <option value="">From Settings</option><option value="or_range">or_range</option><option value="signal_candle">signal_candle</option>
            </select></label>
          <button onClick={start} disabled={job?.status === "running"} data-testid="bt-run-btn"
            className="h-10 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold disabled:opacity-50 col-span-2 md:col-span-1">
            {job?.status === "running" ? `Running ${job.progress}%` : "Run backtest"}
          </button>
        </div>
        {source === "kite" && !kite?.logged_in && <p className="text-xs text-amber-300 mt-2">Kite session not active — log in on the Live tab first, or use SAMPLE data.</p>}
        {job?.status === "running" && job.stage && <p className="text-xs text-slate-400 mt-2" data-testid="bt-stage">{job.stage}…</p>}
        {source === "sample" && <p className="text-xs text-slate-500 mt-2">SAMPLE data is synthetic and only demonstrates the engine — results say nothing about the real strategy.</p>}
        {job?.status === "running" && (
          <div className="mt-3 h-1.5 bg-[#0B111D] rounded" data-testid="bt-progress"><div className="h-full bg-cyan-400 rounded transition-all" style={{ width: `${job.progress}%` }} /></div>
        )}
      </div>
      {run && <button onClick={() => nav(`/history/${run.id}`)} className="text-xs text-cyan-300 hover:underline" data-testid="bt-open-history">Saved to History →</button>}
      {run && <RunReport run={run} />}
    </div>
  );
}
